// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useMemo } from 'react';
import { Alert, Button, Panel } from '@loa/ui-components';
import type { Policy } from '../../types/policy';
import { PolicyList } from '../PolicyList';
import { filterEligibleCoAPolicies, getIneligibleCoAPolicyIds, useCoAWizard } from './CoAWizardContext';

export type CoAPolicySelectionStepProps = Readonly<{
  /** All policies for the relationship, loaded by {@link CoAWizardPage}. */
  policies: Policy[];
  /** Called when the advisor cancels and wants to return to the relationship page */
  onCancel: () => void;
}>;

/**
 * Step 1 of the CoA wizard. Receives the relationship's policies from the
 * parent wizard, filters out policies already in agency, and lets the advisor
 * pick one or more to include in the Change of Agency.
 */
export function CoAPolicySelectionStep({ policies, onCancel }: CoAPolicySelectionStepProps) {
  const { state, togglePolicy, setSelectedPolicies, nextStep } = useCoAWizard();

  const eligible = useMemo(() => filterEligibleCoAPolicies(policies), [policies]);
  const disabledIds = useMemo(() => getIneligibleCoAPolicyIds(policies), [policies]);

  const allSelected =
    eligible.length > 0 && state.selectedPolicyIds.length === eligible.length;

  const toggleAll = () => {
    if (allSelected) {
      setSelectedPolicies([]);
    } else {
      setSelectedPolicies(eligible.map((p) => p.id));
    }
  };

  const handleNext = () => {
    if (state.selectedPolicyIds.length === 0) return;
    nextStep();
  };

  return (
    <div className="p-6">
      <Panel title="Choose the policies">
        {eligible.length === 0 && (
          <div className="px-4 py-4">
            <Alert variant="info" header="No policies eligible for Change of Agency">
              {policies.length === 0
                ? 'This profile does not have any policies yet. Add a policy first.'
                : 'All policies on this profile already have their agency transferred or have a Change of Agency in progress.'}
            </Alert>
          </div>
        )}

        {policies.length > 0 && (
          <PolicyList
            policies={policies}
            loading={false}
            error={null}
            onEdit={() => {}}
            onDelete={() => {}}
            onManageRelationships={() => {}}
            hideActionColumn
            selectable
            selectedPolicyIds={state.selectedPolicyIds}
            onTogglePolicy={togglePolicy}
            onToggleAll={toggleAll}
            allSelected={allSelected}
            disabledPolicyIds={disabledIds}
          />
        )}

        <div className="flex justify-end gap-3 px-4 py-6">
          <Button variant="outlined" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="filled"
            onClick={handleNext}
            disabled={state.selectedPolicyIds.length === 0}
            data-testid="coa-policy-step-next"
          >
            Continue
          </Button>
        </div>
      </Panel>
    </div>
  );
}

export default CoAPolicySelectionStep;
